import './styles.css';
import {useEffect, useState, useCallback, useRef} from 'react';
import {Paper, Grid, Typography, Button} from '@mui/material';
import MicIcon from '@mui/icons-material/Mic';
import CheckCircleOutlineTwoToneIcon from '@mui/icons-material/CheckCircleOutlineTwoTone';
import GraphicEqIcon from '@mui/icons-material/GraphicEq';
import usePitchDetector from "../../hooks/pitchDetectHooks";
import { accidentalDataConvert } from '../../hooks/dataHooks';
import Fingerboard from '../Fingerboard';
import { MIDI_CORRECT_SOUND_CHOICE, MIDI_INCORRECT_SOUND_CHOICE} from '../../utils';
import InvisibleMidiSound from '../InvisibleMidiSound';
import { midiPlaySound } from '../../hooks/midiPlaySound';
import { useAnime } from '../../hooks/AnimeProvider';

const MIDI_CORRECT_LENGTH = 2;
const CLUE_SECONDS_COUNT = 8000;
const Hz_GIVE_WAY = 8;

const PitchTestListener = (props) => {
  const {
    question, 
    questionIndex,
    handleQuestionCorrect, 
    openClueClicked, 
    isSmallScreen, 
    skipNoteClicked,
    showEndLevelModal
  } = props;
  const { pitch, permissionError } = usePitchDetector();
  const [showClueButton, setShowClueButton] = useState(false);
  const [isClueShowing, setIsClueShowing] = useState(false);
  const timeoutID = useRef(null);
  const {anime} = useAnime();
  
  const startShowClueButtonCountDown = useCallback(() => {
    clearTimeout(timeoutID.current);
    timeoutID.current = setTimeout(() => {
      setShowClueButton(true)
		}
		, CLUE_SECONDS_COUNT);
  }, [timeoutID]);
  
  // When the pitch played is correct:
  useEffect(() => {
    if(!showEndLevelModal && !!pitch && pitch <= question.pitchHz + Hz_GIVE_WAY && pitch >= question.pitchHz - Hz_GIVE_WAY ) {
        clearTimeout(timeoutID.current); // Leave in!
        handleQuestionCorrect();   
        midiPlaySound(4, 4, MIDI_CORRECT_SOUND_CHOICE, MIDI_CORRECT_LENGTH)  
    }

  }, [pitch, handleQuestionCorrect, question, showEndLevelModal])

  // Everytime a new question updates:
  useEffect(() => {
    setShowClueButton(false);
    setIsClueShowing(false);
    startShowClueButtonCountDown();

  }, [startShowClueButtonCountDown, questionIndex])

  const handleOpenClueClicked = () => {
    setShowClueButton(false);
    setIsClueShowing(true);
    openClueClicked(question)
  }

  const handleSkipNoteClicked = () => {
    clearTimeout(timeoutID.current); // Leave in!
    skipNoteClicked(question);
  }

  const notePitchDetectUI = (pitch) => {
    return !pitch ? 
      {text: `Listening...`, 
        textColor: ' #8c8c8c', 
        icon: <MicIcon style={{color: '#8c8c8c', minHeight: '90px', marginLeft: '15px', fontSize: 60}}/>, 
        backgroundColor: 'lightGrey' 
      } 
      : pitch <= question.pitchHz + Hz_GIVE_WAY && pitch >= question.pitchHz - Hz_GIVE_WAY ? 
        {text: `Your ${question.noteBaseName}${question.accidental} is correct and in tune!`, 
          textColor: '#008000', 
          icon: <CheckCircleOutlineTwoToneIcon style={{color: 'green', minHeight: '90px', marginLeft: '15px', fontSize: 60}}/>,
          backgroundColor: '#5cd65c'
        }
      :
        {text: `It's not quite right yet...`, 
          textColor: ' #8c8c8c', 
          icon: <GraphicEqIcon style={{color: '#8c8c8c', minHeight: '90px', marginLeft: '15px', fontSize: 60}}/>, 
          backgroundColor: 'lightGrey' 
        } 
  }

  const fingeringTextFinder = (finger) => {
    return finger === '1' ? '1st' :
      finger === '2' ? '2nd' :
        finger === '3' ? '3rd' :
          finger === '4' ? '4th' :
            finger
  }

  return(
    <>
    <InvisibleMidiSound soundChoices={[MIDI_CORRECT_SOUND_CHOICE, MIDI_INCORRECT_SOUND_CHOICE]} />
      <Paper 
        elevation={8} 
        className="pitchTestListenPaper" 
        style={{
          borderRadius:'15px', 
          backgroundColor: 'whitesmoke',
          marginTop: '20px',
          backgroundImage: `url(${anime.url})`
        }}
      >
        <Grid container direction="column" spacing={2}>
          <Grid item>
            <Typography variant={"h6"} className="pitchTestListenerHeader" >Play this note to our PitchListener</Typography>
            <Typography variant="subtitle1"className="pitchTestListenerTextColor" style={{textAlign: 'center'}}>(Ensure your browser mic is on)</Typography>
          </Grid>
          <Grid item>
            <div style={{marginTop: '10px'}}>
              {permissionError ?
                  <Typography color={'red'} variant="h6" style={{textAlign: 'center'}}>
                    Please turn on your browser microphone!
                  </Typography>
                :
                <Grid container direction={'column'} justifyContent={'center'} alignItems={'center'} spacing={2}>
                  <Grid item>
                    <div style={{ backgroundColor: notePitchDetectUI(pitch).backgroundColor}} className="pitchListenerButtonStyle">
                      {notePitchDetectUI(pitch).icon}
                    </div >
                  </Grid>
                  <Grid item>
                    <Typography variant="h6" className="pitchListenerHelperText" style={{ color: notePitchDetectUI(pitch).textColor}}>{notePitchDetectUI(pitch).text}</Typography>
                  </Grid> 
                  {(!!showClueButton && !isClueShowing) &&
                    <Grid item>
                      <Button color="success" variant="outlined" style={{textTransform: 'none'}} onClick={handleOpenClueClicked}>Want some help?</Button>
                    </Grid>    
                  }
                  {!!isClueShowing &&
                   <>
                    <Grid item>
                      <Typography sx={{ mt: 2 }} className="pitchListenerHelperText">The name of this note is: <strong>{question.noteBaseName}{accidentalDataConvert(question.accidental)}</strong>.</Typography>
                      <Typography sx={{ mt: 2 }} className="pitchListenerHelperText">You can play this note on the <strong>{question.chosenString[0]} string</strong> with your <strong>{fingeringTextFinder(question.fingering[0])} finger</strong>.</Typography>
                    </Grid> 
                    <Grid item style={{textAlign: 'center'}}>
                      <Fingerboard 
                        position={question.position[0]}
                        setPosition={null}
                        isSmallScreen={isSmallScreen}
                        isReveal={false}
                        has3rdPos={false}
                        isPitchTest
                      />
                    </Grid>
                    <Grid item>
                      <Button color="secondary" variant="outlined" style={{textTransform: 'none'}} onClick={handleSkipNoteClicked} >Want to skip this note?</Button>
                    </Grid>
                   </>
                  }
                </Grid>
              }
            </div>
          </Grid>
        </Grid>
      </Paper> 
    </>  
  )
}

export default PitchTestListener;